<template>
  <div class="student-email-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Custom Fee Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading custom fee records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Custom Fee Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center">#</th>
              <th class="text-center">GR. NO</th>
              <th class="text-left">STUDENT NAME</th>
              <th class="text-left">FATHER'S NAME</th>
              <th class="text-center">MOBILE NUMBER</th>
              <th class="text-left">ADDRESS</th>
              <th class="text-center">CLASS / SEC</th>
              <th class="text-left">FEE TYPE</th>
              <th class="text-right">AMOUNT</th>
              <th class="text-center">DATE OF JOINING</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportItems" :key="index">
              <td class="text-center font-weight-bold">{{ index + 1 }}</td>
              <td class="text-center gr-cell">{{ item.grNo || "-" }}</td>
              <td class="text-left font-weight-bold">
                {{ (item.studentName || "-").toUpperCase() }}
              </td>
              <td class="text-left">{{ displayFatherName(item) }}</td>
              <td class="text-center">{{ displayMobileNumber(item) }}</td>
              <td class="text-left address-cell">{{ displayAddress(item) }}</td>
              <td class="text-center">
                {{ item.className || "-" }}
                <span v-if="item.sectionName">- {{ item.sectionName }}</span>
              </td>
              <td class="text-left">{{ item.feeType || "-" }}</td>
              <td class="text-right font-weight-bold">
                {{ formatCurrency(item.amount) }}
              </td>
              <td class="text-center">
                {{ formatDate(item.date_of_joining || item.dateOfJoining) }}
              </td>
            </tr>
            <tr v-if="reportItems.length > 0" class="report-total-row">
              <td colspan="8" class="text-right font-weight-bold">
                TOTAL AMOUNT
              </td>
              <td class="text-right font-weight-bold">
                {{ formatCurrency(reportTotalAmount) }}
              </td>
              <td></td>
            </tr>
            <tr v-if="reportItems.length === 0">
              <td colspan="10" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No custom fee records found for the selected criteria</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "CustomFeeReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      reportTotalAmount: null,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      classID: 0,
      sectionID: 0,
      search: "",
      feeTypeID: 0,
      month: 0,
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    classDisplay() {
      if (!this.classID || this.classID == "0") return "All Classes";
      return this.classID;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || 0;
      this.classID = query.classID || "";
      this.sectionID = query.secID || "";
      this.search = query.search || "";
      this.feeTypeID = query.feeType || 0;
      this.month =
        query.month != null && query.month !== "" ? Number(query.month) : 0;

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        // Try fetching from public campuses API first as it's common in this repo
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const url = `${this.$store.state.domain}StudentsCustomFee/GetCustomFeesReport`;

        const params = {
          db: this.db,
          cID: this.cID,
          classID: this.classID || 0,
          secID: this.sectionID || 0,
          search: this.search || "",
          feeType: this.feeTypeID || 0,
          month: this.month || 0,
        };

        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.reportItems = response.data.data || [];
          const total =
            response.data.totalAmount ?? response.data.total_amount;
          this.reportTotalAmount =
            total !== undefined && total !== null ? Number(total) : null;
        } else {
          this.error =
            response.data?.message ||
            "The server returned an unexpected response format.";
        }
      } catch (err) {
        console.error("Error fetching custom fee report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
      } finally {
        this.loading = false;
      }
    },
    displayFatherName(item) {
      const name =
        item.father_name ??
        item.fatherName ??
        item.father ??
        item.FatherName ??
        "";
      return name ? String(name).trim() : "-";
    },
    displayMobileNumber(item) {
      const mobile =
        item.mobileNumber ??
        item.mobile ??
        item.contact_father ??
        item.contactFather ??
        item.contact ??
        item.phone ??
        "";
      return mobile ? String(mobile).trim() : "-";
    },
    displayAddress(item) {
      const address =
        item.address ??
        item.address_current ??
        item.addressCurrent ??
        item.residentialArea ??
        item.studentAddress ??
        "";
      return address ? String(address).trim() : "-";
    },
    formatCurrency(value) {
      if (value === undefined || value === null || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return "-";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    },
    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.student-email-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 8px 10px;
    box-sizing: border-box;
    font-size: 0.85rem;
  }

  thead {
    background: #2c3e50 !important;
    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }

  .address-cell {
    font-size: 0.8rem;
    color: #666;
    max-width: 220px;
    white-space: normal;
    word-break: break-word;
  }

  .report-total-row {
    background: #eef2ff !important;

    td {
      font-size: 0.95rem;
      border-top: 2px solid #2c3e50;
    }
  }

  .email-cell {
    color: #000;
    font-weight: 500;
    font-size: 1rem;
    word-break: break-all;
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .student-email-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;
    .school-logo-img {
      height: 140px !important;
      max-width: 350px !important;
      object-fit: contain !important;
    }
    .school-name-full {
      font-size: 1.8rem !important;
    }
  }

  .summary-section {
    margin: 5px 0 !important;
    padding: 8px !important;
    border: 1px solid #000 !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;

    .summary-card {
      .label {
        font-size: 0.7rem !important;
      }
      .value {
        font-size: 1rem !important;
      }
    }
  }
}
</style>
